import { ChangeDetectionStrategy, Component, computed, inject, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatListItemTitle, MatListOption, MatSelectionList } from '@angular/material/list';
import { MatFormField, MatLabel, MatOption, MatSelect } from '@angular/material/select';
import { NavigationService } from '@common/services/navigation-service';
import { NavigationGroup, NavigationGroupTypes, NavigationSection } from '@common/store/navigation/models/navigation.models';
import { Store } from '@ngrx/store';
import { ModalResponseCodes } from '@src/suite/common/components/modal/modal.service';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { LanguageSettings } from '@src/suite/common/services/language-settings';
import { selectGroups } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectHomeProgram, selectSupervisor } from '@src/suite/common/store/hello-results/hello-results.selectors';
import { selectNavigationGroups } from '@src/suite/common/store/navigation/selectors/navigation.selectors';

@Component({
    selector: 'st-add-view-to-navigation',
    imports: [FormsModule, MatButton, MatFormField, MatIcon, MatLabel, MatListItemTitle, MatListOption, MatOption, MatSelect, MatSelectionList],
    templateUrl: './add-view-to-navigation.html',
    styleUrl: './add-view-to-navigation.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-add-view-to-navigation',
    },
})
export class AddViewToNavigationComponent {
    dialogRef = inject<MatDialogRef<AddViewToNavigationComponent>>(MatDialogRef);
    navigationService = inject(NavigationService);
    globalTablesService = inject(GlobalTablesService);
    languageSettings = inject(LanguageSettings);
    private store = inject(Store);
    data = inject<{
        isPersonalView: boolean;
        vs: StorytellingViewStateRecord;
    }>(MAT_DIALOG_DATA);

    readonly all = computed(() => this.store.selectSignal(selectNavigationGroups)());
    readonly isSupervisor = computed(() => this.store.selectSignal(selectSupervisor)());
    readonly homeProgram = computed(() => this.store.selectSignal(selectHomeProgram)());
    readonly groupDescription = computed(() => this.store.selectSignal(selectGroups)()?.[this.homeProgram()]?.Description);

    readonly groups = computed(() => {
        if (this.isSupervisor() && !this.isPersonalView) {
            return this.all()
                .map((group: NavigationGroup) => {
                    const navigationGroup = { ...group };
                    if (group.ID === NavigationGroupTypes.Team) {
                        navigationGroup.Label = this.groupDescription() ?? group.Label;
                    }
                    return navigationGroup;
                })
                .filter((group: NavigationGroup) => {
                    return group.ID !== NavigationGroupTypes.System;
                });
        } else {
            return this.all().filter((group: NavigationGroup) => {
                return group.ID === NavigationGroupTypes.Personal;
            });
        }
    });

    constructor() {
        const data = this.data;
        if (data.isPersonalView) this.isPersonalView = true;
    }

    get sections(): NavigationSection[] {
        if (this.selectedGroup) {
            return this.selectedGroup.Items;
        }
        return null;
    }

    get isConfirmDisabled(): boolean {
        return !this.selectedGroup || !this.selectedSection;
    }

    public isPersonalView = false;
    public isGroupSelected = false;
    public selectedGroup: NavigationGroup;
    public selectedSection: NavigationSection;

    public onSelectionChange() {
        if (this.selectedGroup) {
            this.isGroupSelected = true;
        } else {
            this.isGroupSelected = false;
        }
    }

    onCancelClick(): void {
        this.dialogRef.close({
            action: ModalResponseCodes.CANCEL,
        });
    }

    onConfirmClick(): void {
        this.dialogRef.close({
            action: ModalResponseCodes.CONFIRM,
            group: this.selectedGroup?.ID,
            section: this.selectedSection[0]?.ID,
            vs: this.data.vs,
        });
    }
}
